import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, X, ArrowRight, Bed, Bath, MapPin, TrendingDown, TrendingUp } from 'lucide-react';
import { getViews, clearViews, syncWithServer } from '../utils/recentlyViewed';
import { propertiesAPI } from '../services/api';
import { localeForCountry, fmtMoney } from '../utils/locale';
import { useMarket } from '../hooks/useMarket';

/**
 * Horizontal-scroll carousel of recently-viewed properties.
 *
 * • Pulled from localStorage (instant render with snapshot data).
 * • Refreshed against the live API in the background so prices stay accurate.
 * • If a property no longer exists (404), it's silently dropped.
 * • Section hides itself entirely if there are zero recents.
 *
 * Mounted on the HomePage between Hero and Market Intel and on Listings page.
 */
const RecentlyViewedRail = ({ title = 'Pick up where you left off', maxItems = 8 }) => {
  const market = useMarket();
  const currentPrefix = market?.url_prefix || '';
  const initialViews = () => getViews().filter((v) => {
    const c = v?.snapshot?.country;
    if (!c) return true;
    return _prefixForCountry(c) === currentPrefix;
  }).slice(0, maxItems);
  const [items, setItems] = useState(initialViews);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      // 1. If logged in, silently pull server views (set on other devices) and
      // merge them into local *before* we hydrate. Falls back to local on error.
      const merged = await syncWithServer();
      if (cancelled) return;
      const rawStart = (merged && merged.length > 0 ? merged : getViews()).slice(
        0,
        maxItems
      );
      // Filter cached views to current market so IE items don't flash on /uk.
      const startList = rawStart.filter((v) => {
        const c = v?.snapshot?.country;
        if (!c) return true;
        return _prefixForCountry(c) === currentPrefix;
      });
      if (startList.length === 0) {
        setItems([]);
        return;
      }
      setItems(startList);

      // 2. Refresh every entry from the live API so prices/images stay accurate
      // and freshly-synced ids (which arrive snapshot-less) get hydrated.
      const fresh = await Promise.all(
        startList.map(async (v) => {
          try {
            const p = await propertiesAPI.getById(v.id);
            if (!p || !(p._id || p.id)) return null;
            return {
              id: v.id,
              viewedAt: v.viewedAt,
              snapshot: {
                id: v.id,
                title: p.title,
                price: p.price,
                // Preserve the immutable "price the user actually saw" so we
                // can compute price-change deltas even after the live refresh.
                priceAtView: v.snapshot?.priceAtView ?? p.price,
                type: p.type,
                propertyType: p.propertyType,
                bedrooms: p.bedrooms,
                bathrooms: p.bathrooms,
                image: (p.images && p.images[0]) || v.snapshot?.image,
                area: p.location?.area || v.snapshot?.area,
                county: p.location?.county || p.location?.city || v.snapshot?.county,
                country: p.location?.country || v.snapshot?.country,
                ber: p.ber || v.snapshot?.ber,
              },
            };
          } catch (_e) {
            // 404 / network — keep the cached snapshot if we have one,
            // otherwise drop the entry.
            return v.snapshot?.title ? v : null;
          }
        })
      );
      if (!cancelled) {
        // Filter to the current market so a Wicklow property viewed earlier
        // doesn't leak onto /uk homepage. `_prefixForCountry` normalises
        // "Ireland" / "United Kingdom" / etc. to '' / '/uk' / '/us' so we
        // can compare that against the current market's URL prefix.
        const currentPrefix = market?.url_prefix || '';
        const kept = fresh.filter(Boolean).filter((v) => {
          const c = v?.snapshot?.country;
          if (!c) return true; // legacy entries without country → keep
          return _prefixForCountry(c) === currentPrefix;
        });
        setItems(kept);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClear = () => {
    clearViews();
    setHidden(true);
  };

  if (hidden || items.length === 0) return null;

  return (
    <section
      className="py-8 sm:py-10 bg-white border-b border-gray-100"
      data-testid="recently-viewed-rail"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-3 mb-4 sm:mb-5">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#5b35a8] font-semibold mb-1">
              <Clock className="h-3 w-3" /> Recently viewed
            </div>
            <h2
              className="text-xl sm:text-2xl font-semibold text-gray-900 tracking-tight"
              style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
            >
              {title}
            </h2>
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="text-xs text-gray-500 hover:text-gray-800 inline-flex items-center gap-1"
            data-testid="recently-viewed-clear"
            aria-label="Clear recently viewed"
          >
            <X className="h-3 w-3" />
            Clear
          </button>
        </div>

        <div className="flex gap-3 sm:gap-4 -mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-2">
          {items.map((it) => (
            <RecentCard key={it.id} item={it} market={market} />
          ))}
        </div>
      </div>
    </section>
  );
};

const _timeAgo = (ts) => {
  if (!ts) return null;
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w}w ago`;
  const mo = Math.floor(d / 30);
  return mo < 12 ? `${mo}mo ago` : `${Math.floor(d / 365)}y ago`;
};

const _formatDeltaShort = (delta, symbol) => {
  const abs = Math.abs(delta);
  if (abs >= 1000) return `${symbol}${Math.round(abs / 1000)}k`;
  return `${symbol}${Math.round(abs)}`;
};

const _prefixForCountry = (country) => {
  const s = (country || '').toLowerCase();
  if (['uk', 'gb', 'united kingdom', 'great britain', 'england', 'scotland', 'wales', 'northern ireland'].includes(s)) return '/uk';
  if (['us', 'usa', 'united states', 'united states of america'].includes(s)) return '/us';
  return '';
};

const RecentCard = ({ item, market }) => {
  const s = item.snapshot || {};
  const price = Number(s.price || 0);
  const priceAtView = s.priceAtView != null ? Number(s.priceAtView) : null;
  const symbol = localeForCountry(s.country).currencySymbol;
  const priceStr = price > 0 ? fmtMoney(price, s.country) : 'POA';
  const badge = ({ sale: 'For Sale', rent: 'To Let', share: 'Share', sold: 'SOLD' }[(s.type || '').toLowerCase()]) || null;

  // Compute price delta (only if we have both numbers and they differ meaningfully)
  let priceChange = null;
  if (priceAtView && price && Math.abs(price - priceAtView) >= 500) {
    const delta = price - priceAtView;
    priceChange = {
      down: delta < 0,
      label: _formatDeltaShort(delta, symbol),
    };
  }
  const timeAgoStr = _timeAgo(item.viewedAt);
  const isStale = item.viewedAt && Date.now() - item.viewedAt > 24 * 3600 * 1000;

  return (
    <Link
      to={`${_prefixForCountry(s.country) || (market?.url_prefix || '')}/property/${s.id}`}
      data-testid={`recent-card-${s.id}`}
      className="snap-start shrink-0 w-[230px] sm:w-[240px] bg-white rounded-xl ring-1 ring-gray-200 hover:ring-[#7042cc]/40 hover:shadow-md transition-all overflow-hidden group"
    >
      <div className="relative h-32 sm:h-36 bg-gray-100">
        {s.image ? (
          <img
            src={s.image}
            alt={s.title || 'Property'}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#7042cc]/15 to-[#FFF7F0]" />
        )}
        {badge && (
          <span className="absolute top-2 left-2 inline-flex items-center px-2 py-0.5 rounded-full bg-white/95 text-[10px] font-bold text-[#5b35a8] shadow-sm">
            {badge}
          </span>
        )}
        {s.ber && (
          <span className="absolute top-2 right-2 inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-600 text-white text-[10px] font-bold shadow-sm">
            {s.ber}
          </span>
        )}
        {/* Eye-catching delta chip — price changed since you last looked */}
        {priceChange && (
          <span
            className={`absolute bottom-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold shadow-md ${
              priceChange.down
                ? 'bg-emerald-600 text-white'
                : 'bg-amber-500 text-white'
            }`}
            data-testid={`recent-card-price-delta-${s.id}`}
          >
            {priceChange.down ? (
              <TrendingDown className="h-3 w-3" />
            ) : (
              <TrendingUp className="h-3 w-3" />
            )}
            {priceChange.down ? `↓ ${priceChange.label}` : `↑ ${priceChange.label}`}
          </span>
        )}
      </div>
      <div className="p-3">
        <div className="flex items-baseline justify-between gap-2">
          <div className="text-[15px] font-extrabold text-gray-900 leading-none">{priceStr}</div>
          {timeAgoStr && (
            <span
              className={`text-[10px] font-medium ${isStale ? 'text-amber-700' : 'text-gray-400'}`}
              data-testid={`recent-card-time-ago-${s.id}`}
            >
              {timeAgoStr}
            </span>
          )}
        </div>
        {/* If price changed AND original price known, show secondary line */}
        {priceChange && priceAtView && (
          <div className="text-[10px] text-gray-500 mt-0.5">
            was{' '}
            <span className={priceChange.down ? 'line-through' : ''}>
              {fmtMoney(priceAtView, s.country)}
            </span>{' '}
            when you viewed
          </div>
        )}
        <div className="mt-1 text-[12px] font-medium text-gray-800 line-clamp-1">
          {s.title || 'Property'}
        </div>
        {(s.area || s.county) && (
          <div className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-gray-500 line-clamp-1">
            <MapPin className="h-3 w-3 shrink-0" />
            {[s.area, s.county].filter(Boolean).join(', ')}
          </div>
        )}
        <div className="mt-1.5 flex items-center gap-2 text-[11px] text-gray-600">
          {s.bedrooms != null && (
            <span className="inline-flex items-center gap-1">
              <Bed className="h-3 w-3" /> {s.bedrooms}
            </span>
          )}
          {s.bathrooms != null && (
            <span className="inline-flex items-center gap-1">
              <Bath className="h-3 w-3" /> {s.bathrooms}
            </span>
          )}
          <span className="ml-auto text-[#7042cc] font-semibold inline-flex items-center gap-0.5 group-hover:translate-x-0.5 transition-transform">
            Re-open <ArrowRight className="h-3 w-3" />
          </span>
        </div>
      </div>
    </Link>
  );
};

export default RecentlyViewedRail;
